import { create } from 'zustand';
import { permissionService, type Permission, type Role, type UserPermissions } from '../services/permission';

export type PermissionType = string;

interface PermissionState {
  // 状态
  permissions: Permission[];
  roles: Role[];
  isLoading: boolean;

  // 操作
  getPermissions: () => Promise<void>;
  getRoles: () => Promise<void>;
  hasPermission: (permission: PermissionType) => boolean;
  hasAnyPermission: (permissions: PermissionType[]) => boolean;
  hasAllPermissions: (permissions: PermissionType[]) => boolean;
  getUserPermissions: (userId?: number) => Promise<UserPermissions>;
  getRolePermissions: (roleId: number) => Promise<number[]>;
  updateRolePermissions: (roleId: number, permissionIds: number[]) => Promise<void>;
  resetRolePermissions: (roleId: number) => Promise<void>;
}

export const usePermission = create<PermissionState>((set, get) => ({
  // 初始状态
  permissions: [],
  roles: [],
  isLoading: false,

  // 获取权限列表
  getPermissions: async () => {
    try {
      set({ isLoading: true });
      const response = await permissionService.getPermissions();
      set({ permissions: response, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      console.error('获取权限列表失败:', error);
    }
  },

  // 获取角色列表
  getRoles: async () => {
    try {
      set({ isLoading: true });
      const response = await permissionService.getRoles();
      set({ roles: response, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      console.error('获取角色列表失败:', error);
    }
  },

  // 检查单个权限
  hasPermission: (permission: PermissionType) => {
    // 从auth store获取用户权限
    const authStore = useAuth.getState();
    return authStore.checkPermission(permission);
  },

  // 检查多个权限（任一满足）
  hasAnyPermission: (permissions: PermissionType[]) => {
    const authStore = useAuth.getState();
    return permissions.some(permission => authStore.checkPermission(permission));
  },

  // 检查多个权限（全部满足）
  hasAllPermissions: (permissions: PermissionType[]) => {
    const authStore = useAuth.getState();
    return authStore.checkPermissions(permissions);
  },

  // 获取用户权限
  getUserPermissions: async (userId?: number) => {
    try {
      const response = await permissionService.getUserPermissions(userId);
      return response;
    } catch (error) {
      console.error('获取用户权限失败:', error);
      throw error;
    }
  },

  // 获取角色权限
  getRolePermissions: async (roleId: number) => {
    try {
      const response = await permissionService.getRoleDetail(roleId);
      return response.permission_ids;
    } catch (error) {
      console.error('获取角色权限失败:', error);
      throw error;
    }
  },

  // 更新角色权限
  updateRolePermissions: async (roleId: number, permissionIds: number[]) => {
    try {
      await permissionService.updateRolePermissions(roleId, permissionIds);
    } catch (error) {
      console.error('更新角色权限失败:', error);
      throw error;
    }
  },

  // 重置角色权限
  resetRolePermissions: async (roleId: number) => {
    try {
      await permissionService.resetRolePermissions(roleId);
    } catch (error) {
      console.error('重置角色权限失败:', error);
      throw error;
    }
  },
}));

// 导入useAuth以避免循环依赖
import { useAuth } from './auth';